import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Drawer,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useEffect, useMemo, useState } from 'react'
import {
  listDietPlanConditions,
  listDietPlanTemplates,
} from 'src/mentor/services/dietPlanService'
import {
  DIETARY_FLAG_OPTIONS,
  LANGUAGE_OPTIONS,
  conditionLabel,
} from './dietPlanLabels'

/**
 * Side drawer to browse the diet-plan template catalogue and pick one to customise.
 *
 * The picker is a thin shell over the catalogue API: condition + dietary preference + language
 * are passed through as query params so the server returns the appropriate localised list.
 * Selecting a card calls {@code onPickTemplate(template, language)} — the parent then opens the
 * editor with that template as the starting draft.
 */
export default function DietPlanTemplatePicker({
  open,
  onClose,
  onPickTemplate,
  initialLanguage = 'en',
  initialDietaryFlag = '',
  initialCondition = '',
}) {
  const [language, setLanguage] = useState(initialLanguage)
  const [dietaryFlag, setDietaryFlag] = useState(initialDietaryFlag)
  const [condition, setCondition] = useState(initialCondition)
  const [conditionsAvailable, setConditionsAvailable] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [errorText, setErrorText] = useState('')

  // Conditions are loaded once when the drawer opens. They drive the chip filter row.
  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    ;(async () => {
      try {
        const codes = await listDietPlanConditions()
        if (!cancelled) setConditionsAvailable(codes)
      } catch (err) {
        if (!cancelled) console.error('Failed to load diet plan conditions', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open])

  // Templates re-fetch whenever filters change — kept simple, the catalogue is small.
  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    setLoading(true)
    setErrorText('')
    ;(async () => {
      try {
        const { templates: rows } = await listDietPlanTemplates({
          condition: condition || undefined,
          dietaryFlag: dietaryFlag || undefined,
          language,
        })
        if (!cancelled) setTemplates(rows)
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load diet plan templates', err)
          setErrorText('Could not load templates. Please try again.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, condition, dietaryFlag, language])

  const conditionChips = useMemo(() => {
    // Condition labels follow the seeded codes; unknown ones still show by their raw code.
    return conditionsAvailable.map((c) => ({ code: c, label: conditionLabel(c) }))
  }, [conditionsAvailable])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 720 }, maxWidth: '95vw' } }}
    >
      <Toolbar sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
          Pick a diet plan template
        </Typography>
        <IconButton onClick={onClose} aria-label="Close template picker">
          <CloseIcon />
        </IconButton>
      </Toolbar>

      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            size="small"
            sx={{ minWidth: 160 }}
          >
            {LANGUAGE_OPTIONS.map((l) => (
              <MenuItem key={l.code} value={l.code}>
                {l.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Dietary preference"
            value={dietaryFlag}
            onChange={(e) => setDietaryFlag(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Any</MenuItem>
            {DIETARY_FLAG_OPTIONS.map((f) => (
              <MenuItem key={f.code} value={f.code}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            Condition
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              label="All"
              variant={condition === '' ? 'filled' : 'outlined'}
              color={condition === '' ? 'primary' : 'default'}
              onClick={() => setCondition('')}
              size="small"
            />
            {conditionChips.map((c) => (
              <Chip
                key={c.code}
                label={c.label}
                variant={condition === c.code ? 'filled' : 'outlined'}
                color={condition === c.code ? 'primary' : 'default'}
                onClick={() => setCondition(c.code)}
                size="small"
              />
            ))}
          </Stack>
        </Box>

        {loading && <LinearProgress />}
        {errorText && (
          <Typography variant="body2" color="error">
            {errorText}
          </Typography>
        )}

        {!loading && templates.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No templates match the current filters.
          </Typography>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {templates.map((template) => (
            <TemplateCard
              key={template.id ?? `${template.code}-${template.version}`}
              template={template}
              onPick={() => onPickTemplate?.(template, language)}
            />
          ))}
        </Box>
      </Box>
    </Drawer>
  )
}

function TemplateCard({ template, onPick }) {
  const conditions = template?.targetConditions ?? []
  const tags = template?.tags ?? []
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardActionArea onClick={onPick} sx={{ p: 2, alignItems: 'flex-start', height: '100%' }}>
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {template.title}
          </Typography>
          {template.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {template.description}
            </Typography>
          )}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
            {conditions.map((c) => (
              <Chip key={c} size="small" label={conditionLabel(c)} variant="outlined" color="primary" />
            ))}
          </Stack>
          {tags.length > 0 && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              {tags.map((t) => (
                <Chip key={t} size="small" label={t} variant="outlined" />
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            {template.approxCaloriesPerDay != null && (
              <Typography variant="caption" color="text.secondary">
                ~{template.approxCaloriesPerDay} kcal/day
              </Typography>
            )}
            {template.approxMacroSplit && (
              <Typography variant="caption" color="text.secondary">
                {template.approxMacroSplit}
              </Typography>
            )}
          </Stack>
          <Button size="small" sx={{ mt: 1, textTransform: 'none' }}>
            Customise →
          </Button>
        </Box>
      </CardActionArea>
    </Card>
  )
}
